import SmallLoader from "../../../../loaders/SmallLoader"
import { navigateTo } from "../../../../navigationService/navigationServiceFunctions"

const pdfFileIconSrc = require('../../../../../assets/images/fontAwesomeIcons/file-pdf-solid.svg').default
const excelFileIconSrc = require('../../../../../assets/images/fontAwesomeIcons/file-excel-solid.svg').default
const imageFileIconSrc = require('../../../../../assets/images/fontAwesomeIcons/file-image-solid.svg').default
const wordFileIconSrc = require('../../../../../assets/images/fontAwesomeIcons/file-word-solid.svg').default
const zipFileIconSrc = require('../../../../../assets/images/fontAwesomeIcons/file-zipper-solid.svg').default
const fileIconSrc = require('../../../../../assets/images/fontAwesomeIcons/file-solid.svg').default
const previewAvailableIconSrc = require('../../../../../assets/images/fontAwesomeIcons/preview-available.svg').default
const noPreviewIconSrc = require('../../../../../assets/images/fontAwesomeIcons/no-preview.svg').default
const downloadAvailableIconSrc = require('../../../../../assets/images/fontAwesomeIcons/download-available.svg').default
const downloadSuccessIconSrc = require('../../../../../assets/images/fontAwesomeIcons/download-success.svg').default
const downloadFailIconSrc = require('../../../../../assets/images/fontAwesomeIcons/download-fail.svg').default
const noDownloadIconSrc = require('../../../../../assets/images/fontAwesomeIcons/no-download.svg').default

const PDFFileExtensions = ['pdf', '.pdf']
const EXCELFileExtensions = ['xlsx', '.xlsx', 'xls', '.xls']
const ImageFileExtensions = ['jpg', '.jpg', 'jpeg', '.jpeg', 'png', '.png', 'gif', '.gif', 'tiff', '.tiff', 'svg', '.svg']
const DOCXFileExtensions = ['docx', '.docx']
const ArchiveFileExtensions = ['zip', '.zip', 'rar', '.rar']

function formatBytes(bytes: any) {
    if (typeof bytes === 'string') {
        bytes = parseFloat(bytes.replace(/\s/g, ''))
    }
    let marker = 1024; // Change to 1000 if required
    let decimal = 2; // Change as required
    let kiloBytes = marker; // One Kilobyte is 1024 bytes
    let megaBytes = marker * marker; // One MB is 1024 KB
    let gigaBytes = marker * marker * marker; // One GB is 1024 MB

    // return bytes if less than a KB
    if (bytes < kiloBytes) return bytes + " Б";
    // return KB if less than a MB
    else if (bytes < megaBytes) return (bytes / kiloBytes)?.toFixed(decimal) + " KБ";
    // return MB if less than a GB
    else if (bytes < gigaBytes) return (bytes / megaBytes)?.toFixed(decimal) + " MБ";
    // return GB if less than a TB
    else return (bytes / gigaBytes)?.toFixed(decimal) + " ГБ";
}

export function fileSizeBodyTemplate(rowData: any, field: any) {
    const value = rowData[field]
    return (
        <div>
            {value
                ?
                <div>{formatBytes(value)}</div>
                :
                <div>—</div>
            }
        </div>
    );
}

// Шаблон ячейки таблицы для отображения соответствующей иконки
export function fileIconBodyTemplate(rowData: any, field: any) {
    const extension = rowData[field]?.toLowerCase()
    let imageSrc
    if (PDFFileExtensions.includes(extension)) {
        imageSrc = pdfFileIconSrc
    } else if (EXCELFileExtensions.includes(extension)) {
        imageSrc = excelFileIconSrc
    } else if (DOCXFileExtensions.includes(extension)) {
        imageSrc = wordFileIconSrc
    } else if (ImageFileExtensions.includes(extension)) {
        imageSrc = imageFileIconSrc
    } else if (ArchiveFileExtensions.includes(extension) || rowData['LinkType'] === 'Folder') {
        imageSrc = zipFileIconSrc
    } else {
        imageSrc = fileIconSrc
    }
    return (
        <img className="file-type-icon" src={imageSrc} />
    );
}

interface FilePreviewBodyTemplateOptions {
    propForDocumentId: string;
    propForFileExtension: string;
    previewFunction: (
        id: string | number,
    ) => void;
}

export function buildFilePreviewBodyTemplate({
    propForDocumentId,
    propForFileExtension,
    previewFunction
}: FilePreviewBodyTemplateOptions) {
    return function filePreviewBodyTemplate(rowData: any, field: string) {
        const documentId = rowData?.[propForDocumentId];
        const extension = rowData[propForFileExtension]?.toLowerCase();
        const extensionIsAcceptable = PDFFileExtensions.includes(extension) || DOCXFileExtensions.includes(extension) || ImageFileExtensions.includes(extension);
        const canBePreviewed = extensionIsAcceptable && Boolean(rowData[field]);
        const imageSrc = canBePreviewed ? previewAvailableIconSrc : noPreviewIconSrc;
        const onClickFunction = canBePreviewed ? () => previewFunction(documentId) : undefined;
        return (
            documentId
                ?
                <img className="file-preview-icon" src={imageSrc} onClick={onClickFunction} />
                :
                <>—</>
        );
    }
}

interface FileDownloadBodyTemplateOptions {
    propForDocumentId: string;
    propForFileExtension: string;
    propForDocumentName: string;
    propForLinkType?: string;
    downloadFunction: (
        id: string | number,
        ext: string,
        name: string,
        linkType: string
    ) => void;
    documentsDownloadingState: Record<string | number, 'isLoading' | 'isError' | 'isSuccess' | undefined>;
}

export function buildFileDownloadBodyTemplate({
    propForDocumentId,
    propForFileExtension,
    propForDocumentName,
    propForLinkType,
    downloadFunction,
    documentsDownloadingState,
}: FileDownloadBodyTemplateOptions) {
    return function fileDownloadBodyTemplate(rowData: any, field: string) {
        const canBeDownloaded = Boolean(rowData[field]);

        const documentId = rowData?.[propForDocumentId];
        const fileExt = rowData?.[propForFileExtension];
        const documentName = rowData?.[propForDocumentName];
        const linkType = rowData?.[propForLinkType];

        const state = documentsDownloadingState?.[documentId];

        const iconMap: Record<string, string> = {
            isError: downloadFailIconSrc,
            isSuccess: downloadSuccessIconSrc,
            default: canBeDownloaded ? downloadAvailableIconSrc : noDownloadIconSrc,
        };

        const imageSrc = iconMap[state as keyof typeof iconMap] ?? iconMap.default;

        const handleClick =
            canBeDownloaded
                ? () => downloadFunction(documentId, fileExt, documentName, linkType)
                : undefined;

        return (
            documentId
                ?
                <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    {state === 'isLoading' ? (
                        <SmallLoader iconColor="black" />
                    ) : (
                        <img
                            id={`load${documentId}`}
                            className="file-download-icon"
                            src={imageSrc}
                            onClick={handleClick}
                        />
                    )}
                </div>
                :
                <>—</>
        );
    };
}